import { ExpandCloseIco, ExpandOnIco } from "../../assets/customIcons";
import {
  ExpandableConfig,
  GetComponentProps,
  RowClassName,
} from "rc-table/lib/interface";
import { NotFound, SortIcon, Spinner } from "../../ui-kit";
import React, { ReactNode } from "react";
import { SpinProps, Table, TableColumnType, TableColumnsType } from "antd";

import { ColumnsType } from "antd/lib/table";
import { GetRowKey } from "antd/lib/table/interface";
import { Sorter } from "../../store/core";
import { SortingDirection } from "../../constants/Enums";
import { TableProps } from "antd/es/table";
import classnames from "classnames";
import styles from "./ExpandedTable.module.scss";
import Constants from "../../constants/Common";

export type ITableLayout = "fixed" | "auto";
export interface IAntTableScroll {
  scrollToFirstRowOnChange?: boolean;
  x?: string | number | true;
  y?: string | number;
}

export interface IExpandedTable<T> extends TableProps<T> {
  paginationBool?: boolean;
  className?: string;
  expandElementClassName?: string;
  rowClassName?: string | RowClassName<T>;
  onRow?: GetComponentProps<T>;
  onExpand?: (expanded: boolean, record: T) => void;
  expandRowByClick?: boolean;
  rowKeys?: (string | number)[];
  columns?: ColumnsType<T>;
  data?: T[];
  children?: React.ReactNode;
  setRowKeys?: (rows) => void;
  defaultExpandAllRows?: boolean;
  rowKey?: GetRowKey<T>;
  rowExpandable?: (record: T) => boolean;
  expandedRowRender?: (record: T, index, indent, expanded) => ReactNode | null;
  rowsLength?: number;
  loading?: boolean | SpinProps;
  sort?: Sorter;
  rowSelection?: any;
  size?: "middle" | "small" | "large";
  scroll?: IAntTableScroll;
  indentSize?: number;
  key?: string;
  bordered?: boolean;
  expandable?: ExpandableConfig<T>;
  title?: string;
  tableLayout?: ITableLayout;
  components?: any;
}

export const ExpandedTable = <T extends object>({
  paginationBool = false,
  className,
  expandElementClassName,
  rowClassName = "",
  onRow = () => ({}),
  onExpand = () => ({}),
  expandRowByClick = true,
  rowKeys,
  setRowKeys = (rows) => ({}),
  columns,
  data,
  children,
  defaultExpandAllRows = false,
  rowKey = (record) => record?.id,
  rowExpandable,
  expandedRowRender = null,
  rowsLength = 0,
  loading = false,
  sort,
  rowSelection,
  size = "middle",
  scroll,
  indentSize,
  key,
  bordered = false,
  expandable = null,
  title = "",
  tableLayout = "auto",
  components = null,
  ...rest
}: IExpandedTable<T>) => {
  const parseCol = (col) => {
    let children = null;
    if (col.children?.length > 0) {
      children = col.children.map(parseCol);
    }
    const newCol: TableColumnType<any> = {
      ...col,
      title: col.sortable
        ? ({ sortColumns }) => {
            // const sortedColumn = sortColumns?.find(({ column }) => {
            //   if (
            //     Array.isArray(column.dataIndex) &&
            //     Array.isArray(col.dataIndex)
            //   ) {
            //     return column.dataIndex.join("") === col.dataIndex.join("");
            //   }
            //   return column.dataIndex === col.dataIndex;
            // });
            const sortedColumn =
              sort?.sortData.find((c) => {
                if (Array.isArray(col.dataIndex)) {
                  return c.fieldName === col.dataIndex.join(".");
                }
                return c.fieldName === col.dataIndex;
              }) || null;
            const sortDir =
              sortedColumn !== null
                ? sortedColumn.direction === SortingDirection.Desc
                  ? Constants.sortDirDesc
                  : Constants.sortDirAsc
                : "";
            return (
              <>
                {/*<SortIcon direction={sortedColumn?.order} /> {col.title}*/}
                <SortIcon direction={sortDir} /> {col.title}
              </>
            );
          }
        : col.title,
      sorter: col.sortable,
      sortDirections: col.sortable
        ? [Constants.sortDirAsc, Constants.sortDirDesc, Constants.sortDirAsc]
        : null,
      children: children,
    };
    return newCol;
  };

  const cols: TableColumnsType = columns?.map(parseCol);

  const onChangeTable = (newPagination, filters, sorter) => {
    const e = window.event as PointerEvent;
    if (!e?.ctrlKey) {
      sort?.setSortData(
        Array.isArray(sorter.field) ? sorter.field.join(".") : sorter.field,
        sorter.order === Constants.sortDirAsc
          ? SortingDirection.Asc
          : SortingDirection.Desc,
      );
    } else {
      sort?.addSortItem(
        Array.isArray(sorter.field) ? sorter.field.join(".") : sorter.field,
        sorter.order === Constants.sortDirAsc
          ? SortingDirection.Asc
          : SortingDirection.Desc,
      );
    }
  };

  const hasChildren = children || expandedRowRender;

  const expandableExpandTable = hasChildren
    ? {
        columnWidth: "5%",
        onExpand: onExpand,
        defaultExpandAllRows: defaultExpandAllRows,
        expandRowByClick,
        onExpandedRowsChange: (rows) => setRowKeys(rows),
        expandedRowKeys: rowKeys,
        expandedRowClassName: () => "expanded-row-cell",
        expandedRowRender: (record, index, indent, expanded) => {
          return (
            <div
              className={classnames(styles.expanded, {
                [expandElementClassName]: expandElementClassName,
              })}
            >
              {!!expandedRowRender
                ? expandedRowRender(record, index, indent, expanded)
                : children
                ? React.cloneElement(children, { record })
                : ""}
            </div>
          );
        },
        expandIcon: ({ expanded, onExpand, record }) =>
          expanded ? (
            <ExpandOnIco onClick={(e) => onExpand(record, e)} />
          ) : (
            <ExpandCloseIco onClick={(e) => onExpand(record, e)} />
          ),
      }
    : null;

  const tableLoading: SpinProps = {
    spinning: loading as boolean,
    indicator: <Spinner size="large" />,
  };

  return (
    <Table<T>
      className={classnames(styles.tab, {
        [className]: className,
        [styles.emptyTab]: !data?.length,
      })}
      rowClassName={rowClassName}
      columns={cols}
      dataSource={data}
      loading={tableLoading}
      onRow={onRow}
      rowKey={rowKey}
      rowExpandable={rowExpandable}
      expandable={expandable ? expandable : expandableExpandTable}
      rowSelection={rowSelection}
      locale={{
        emptyText: <NotFound />,
        triggerDesc: "Нажмите для сортировки",
        triggerAsc: "Нажмите для сортировки",
        cancelSort: "Нажмите, чтобы сбросить сортировку",
      }}
      bordered={bordered}
      pagination={paginationBool}
      onChange={onChangeTable}
      size={size}
      scroll={scroll}
      indentSize={indentSize}
      key={key}
      title={() => `${title}`}
      tableLayout={tableLayout}
      components={components}
      {...rest}
    />
  );
};
